import { useState, useCallback, useEffect, useRef } from "react";
import { Github, RefreshCw, Check, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

interface GitHubRepoAccessData {
  repo_full_name: string;
  settings_url: string;
  error?: string;
}

interface GitHubRepoAccessWidgetProps {
  data: GitHubRepoAccessData;
  onAction?: (actionId: string, context?: Record<string, unknown>) => void;
}

export function GitHubRepoAccessWidget({ data, onAction }: GitHubRepoAccessWidgetProps) {
  const [status, setStatus] = useState<"idle" | "waiting" | "retrying" | "resolved">("idle");
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  // After the user completes the GitHub flow, poll to verify access was restored
  const startPolling = useCallback(() => {
    setStatus("waiting");
    let attempts = 0;
    const maxAttempts = 60;

    if (intervalRef.current) clearInterval(intervalRef.current);

    intervalRef.current = setInterval(async () => {
      attempts++;
      try {
        // Check if GitHub status has updated
        const result = await api.github.status();
        if (result.connected && result.installations.length > 0) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          // Auto-retry push
          setStatus("retrying");
          onAction?.("push", { retry: true });
          setTimeout(() => setStatus("resolved"), 2000);
          return;
        }
      } catch {
        // Keep waiting
      }
      if (attempts >= maxAttempts) {
        if (intervalRef.current) clearInterval(intervalRef.current);
        setStatus("idle");
      }
    }, 2000);
  }, [onAction]);

  // Listen for cross-tab signal from OAuth popup
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === "github-connected" && status === "waiting") {
        if (intervalRef.current) clearInterval(intervalRef.current);
        setStatus("retrying");
        onAction?.("push", { retry: true });
        setTimeout(() => setStatus("resolved"), 2000);
      }
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [status, onAction]);

  function handleReconnect() {
    const popup = window.open(api.github.oauthUrl(), "github-oauth", "popup");
    if (!popup || popup.closed) {
      window.location.href = api.github.oauthUrl();
      return;
    }
    startPolling();
  }

  function handleRetry() {
    setStatus("retrying");
    onAction?.("push", { retry: true });
    setTimeout(() => setStatus("idle"), 5000);
  }

  if (status === "resolved") {
    return (
      <Card variant="success" padding="sm" className="max-w-lg animate-slide-up">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-daydra-success/10">
            <Check size={16} className="text-daydra-success" />
          </div>
          <p className="text-sm font-medium text-daydra-text">Reconnected — retrying push</p>
        </div>
      </Card>
    );
  }

  return (
    <Card variant="error" padding="none" className="overflow-hidden max-w-lg animate-slide-up">
      <div className="flex items-start gap-3 px-4 py-3">
        <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-daydra-error/10 shrink-0 mt-0.5">
          <Github size={16} className="text-daydra-error" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-daydra-text">
            GitHub App can't access this repo
          </p>
          <p className="text-xs text-daydra-dim mt-1">
            <span className="font-mono text-daydra-text/80">{data.repo_full_name}</span> isn't
            in the GitHub App's repo list. Reconnect to update your repo selection.
          </p>
        </div>
      </div>

      <div className="flex gap-2 px-4 py-3 border-t border-daydra-border">
        {status === "waiting" ? (
          <Button variant="default" size="sm" disabled>
            <Loader2 size={14} className="animate-spin" />
            Waiting for GitHub...
          </Button>
        ) : status === "retrying" ? (
          <Button variant="default" size="sm" disabled>
            <Loader2 size={14} className="animate-spin" />
            Pushing...
          </Button>
        ) : (
          <>
            <Button variant="default" size="sm" onClick={handleReconnect}>
              <Github size={14} />
              Reconnect GitHub
            </Button>
            <Button variant="secondary" size="sm" onClick={handleRetry}>
              <RefreshCw size={14} />
              Retry push
            </Button>
          </>
        )}
      </div>
    </Card>
  );
}
